import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Container, Group, Paper, PasswordInput, Stack, Text, Title } from '@mantine/core'
import { AppLayout } from '../features/projects/app-shell'
import { refreshMe, useAuthStore } from '../features/auth/store'
import { changePassword, logout } from '../shared/api/auth'
import { isApiError } from '../shared/api/client'

// Зеркало PASSWORD_MIN_LENGTH из @ledokol/contracts (ChangePasswordRequest). Web импортирует из
// contracts только типы: пакет собран в CommonJS, и vite-dev не отдаёт его именованные значения в ESM.
const PASSWORD_MIN_LENGTH = 10

const ERRORS: Record<string, string> = {
  invalid_current_password: 'Текущий пароль неверен',
  password_reused: 'Новый пароль совпадает с текущим',
  password_not_set: 'У аккаунта нет пароля: вход через Google или Telegram',
  too_many_requests: 'Слишком много попыток, подождите минуту',
}

/**
 * Смена своего пароля. Тот же экран — принудительный после админского сброса
 * (AuthGate не выпускает отсюда, пока стоит must_change_password).
 */
export function MePasswordPage() {
  const user = useAuthStore((s) => s.user)
  const forced = !!user?.must_change_password
  const navigate = useNavigate()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const tooShort = next.length > 0 && next.length < PASSWORD_MIN_LENGTH
  const mismatch = repeat.length > 0 && repeat !== next
  const canSave = current.length > 0 && next.length >= PASSWORD_MIN_LENGTH && next === repeat

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSave) return
    setSaving(true)
    setError(null)
    try {
      await changePassword(current, next)
      await refreshMe()
      setCurrent('')
      setNext('')
      setRepeat('')
      if (forced) navigate('/projects', { replace: true })
      else setDone(true)
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status
      const code = status === 429 ? 'too_many_requests' : isApiError(err) ? err.response?.data.error.code : undefined
      setError((code && ERRORS[code]) ?? 'Не удалось сменить пароль')
    } finally {
      setSaving(false)
    }
  }

  async function onLogout() {
    await logout()
    useAuthStore.getState().setUser(null)
    navigate('/login', { replace: true })
  }

  // Принудительный режим — без AppLayout: его меню/поиск/колокольчик ходят в API и получили бы
  // 403 password_change_required (тосты об ошибках, бесполезные ссылки). Только форма и выход.
  const body = (
      <Container size="xs" py={forced ? 'xl' : 'md'}>
        <Group justify="space-between" mb="md">
          <Title order={2}>Смена пароля</Title>
          {forced && (
            <Button variant="subtle" color="red" onClick={() => void onLogout()}>
              Выйти
            </Button>
          )}
        </Group>
        <Paper withBorder p="md" radius="md">
          <form onSubmit={submit}>
            <Stack>
              {forced && (
                <Alert color="orange">
                  Администратор сбросил пароль. Чтобы продолжить работу, задайте новый.
                </Alert>
              )}
              {done && <Alert color="green">Пароль изменён. Остальные сессии завершены.</Alert>}
              {error && <Alert color="red">{error}</Alert>}
              <PasswordInput label="Текущий пароль" value={current} onChange={(e) => setCurrent(e.currentTarget.value)} autoComplete="current-password" required />
              <PasswordInput
                label="Новый пароль"
                description={`Минимум ${PASSWORD_MIN_LENGTH} символов`}
                value={next}
                onChange={(e) => setNext(e.currentTarget.value)}
                error={tooShort ? `Минимум ${PASSWORD_MIN_LENGTH} символов` : undefined}
                autoComplete="new-password"
                required
              />
              <PasswordInput
                label="Повторите новый пароль"
                value={repeat}
                onChange={(e) => setRepeat(e.currentTarget.value)}
                error={mismatch ? 'Пароли не совпадают' : undefined}
                autoComplete="new-password"
                required
              />
              <Text size="xs" c="dimmed">После смены все остальные сессии будут завершены.</Text>
              <Button type="submit" loading={saving} disabled={!canSave}>Сменить пароль</Button>
            </Stack>
          </form>
        </Paper>
      </Container>
  )
  return forced ? body : <AppLayout>{body}</AppLayout>
}
